import * as React from "react"
import { 
  TextInput, 
  ImageInput, 
  ImageField, 
  ArrayInput, 
  SimpleFormIterator,
  useNotify,
} from "react-admin"
import { useEffect, useRef, useState, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Grid, Stack, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material"
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import isEqual from "lodash/isEqual"; 
import HexColorTextInput from "./HexColorTextInput";

const ColorOptionsTab = () => {
  const { setValue, getValues } = useFormContext();
  const notify = useNotify();

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");

  const handleCopyAll = async () => {
    const rows = (getValues('colors') || [])
      .map(c => {
        const code = (c?.code ?? '').toString().replace(/^#/, '').toUpperCase();
        const name = (c?.name ?? '').toString().trim();
        if (!/^[0-9A-F]{6}$/.test(code)) return null;
        if (!name) return null; // 沒名稱就不複製
        return `${code},${name}`;
      })
      .filter(Boolean)
      .join('\n');

    if (!rows) {
      notify('沒有可複製的色碼', { type: 'warning' });
      return;
    }

    try {
      await navigator.clipboard.writeText(rows);
      notify('已複製全部色碼', { type: 'info' });
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = rows;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        notify('已複製全部色碼', { type: 'info' });
      } catch {
        notify('複製失敗，請手動複製', { type: 'warning' });
      }
    }
  };

  const sanitizeHex = (v) =>
    (v || "").toString().replace(/[^0-9a-fA-F]/g, "").slice(0, 6).toUpperCase();
  const normName = (v) => String(v ?? "").trim().toLowerCase();

  const handleBulkApply = () => {
    const lines = bulkText.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (!lines.length) {
      notify('沒有可匯入的內容', { type: 'warning' });
      return;
    }

    const splitOnceByComma = (s) => {
      const m = /[,，]/.exec(s); // 第一個逗號（半/全形）
      if (!m) return [s, ""];
      const i = m.index;
      return [s.slice(0, i), s.slice(i + m[0].length)];
    };

    // 解析 + 基本完整性檢查（HEX + 名稱都要有）
    const parsed = lines.map((line, i) => {
      const [rawHex, rawName] = splitOnceByComma((line || "").trim());
      const hex = sanitizeHex(String(rawHex ?? "").replace(/^#/, ""));
      const nameRaw = String(rawName ?? "").trim();
      const name = normName(nameRaw);
      const valid = /^[0-9A-F]{6}$/.test(hex) && !!name;
      return { lineNo: i + 1, hex, nameRaw, name, valid };
    });

    const invalid = parsed.filter(p => !p.valid);
    if (invalid.length) {
      const msg = invalid.map(p => `第 ${p.lineNo} 行填寫不完整！`).join('\n');
      notify(msg, { type: 'warning' });
      return; // 有任何遺漏就不帶入
    }

    // 先檢查「匯入內容內部」是否有 HEX 或 名稱 重複（任一重複就中止）
    const seenHex = new Map();   // hex
    const seenName = new Map();  // name
    parsed.forEach(p => {
      if (!seenHex.has(p.hex)) seenHex.set(p.hex, []);
      seenHex.get(p.hex).push(p.lineNo);
      if (!seenName.has(p.name)) seenName.set(p.name, []);
      seenName.get(p.name).push(p.lineNo);
    });

    const dupHexLines = [...seenHex.values()].filter(arr => arr.length > 1).flat();
    const dupNameLines = [...seenName.values()].filter(arr => arr.length > 1).flat();

    if (dupHexLines.length || dupNameLines.length) {
      const parts = [];
      if (dupHexLines.length) parts.push(`匯入內容有「色碼」重複：第 ${[...new Set(dupHexLines)].sort((a,b)=>a-b).join('、')} 行`);
      if (dupNameLines.length) parts.push(`匯入內容有「名稱」重複：第 ${[...new Set(dupNameLines)].sort((a,b)=>a-b).join('、')} 行`);
      notify(`批次新增失敗：\n${parts.join('\n')}`, { type: 'warning' });
      return;
    }

    // 取出表單現有資料，建立現有的 HEX / 名稱 集合（名稱採不分大小寫比較）
    const existing = Array.isArray(getValues('colors')) ? [...getValues('colors')] : [];
    const existingHexSet = new Set(existing.map(r => sanitizeHex(String(r?.code || '').replace(/^#/, ''))).filter(Boolean));
    const existingNameSet = new Set(existing.map(r => normName(r?.name)).filter(Boolean));

    // 檢查「與現有資料」是否有 HEX 或 名稱 衝突（任一重複就整批中止）
    const conflictLines = [];
    parsed.forEach(p => {
      if (existingHexSet.has(p.hex) || existingNameSet.has(p.name)) {
        conflictLines.push(p.lineNo);
      }
    });

    if (conflictLines.length) {
      notify(`批次新增失敗：與現有資料重複（色碼或名稱已存在）。重複行：第 ${[...new Set(conflictLines)].sort((a,b)=>a-b).join('、')} 行`, { type: 'warning' });
      return;
    }

    // 沒衝突就整批新增
    const addedRecords = parsed.map(({ hex, nameRaw }) => ({ code: hex, name: nameRaw }));
    const next = existing.concat(addedRecords);

    setValue('colors', next, { shouldDirty: true, shouldValidate: true });
    setBulkOpen(false);
    setBulkText("");

    notify(`批次處理完成：新增 ${addedRecords.length}，跳過 0`, { type: 'info' });
  };


  return (
    <Grid container width={{ xs: "100%", xl: 800 }} spacing={2}>
      <Grid item xs={12}>
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mb: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PlaylistAddIcon />}
            onClick={() => setBulkOpen(true)}
          >
            批次新增色碼
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyAll}
          >
            複製全部色碼
          </Button>
        </Stack>

        <ArrayInput source="colors" label="顏色選項">
          <SimpleFormIterator getItemLabel={index => `#${index + 1}`} inline>
            <HexColorTextInput source="code" label="HEX色碼"/>
            <TextInput source="name" label="選項名稱"/>
          </SimpleFormIterator>
        </ArrayInput>
      </Grid>

      {/* 批次新增對話框 */}
      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>批次新增色碼</DialogTitle>
        <DialogContent dividers>
          <TextField
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder={`每行一筆，格式：HEX,名稱\n例如：\nFFFFFF,白色\n000000,黑色`}
            multiline
            minRows={6}
            fullWidth
            InputProps={{ sx: { fontFamily: 'monospace' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)}>取消</Button>
          <Button variant="contained" onClick={handleBulkApply}>套用</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  )
}

export default ColorOptionsTab;
